import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Text, ScrollView, ActivityIndicator, TouchableOpacity, Image, Modal, TextInput, Alert } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { Ionicons } from '@expo/vector-icons';
import reportService from '../services/reportService';
import InteractiveMapView from '../components/maps/InteractiveMapView';
import ReportTags from '../components/reports/ReportTags';

const ReportDetailScreen = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const { user } = useSelector(state => state.auth);
  const reportId = route.params?.reportId;
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ map: true }); // Mapa expandido por defecto
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Cargar el reporte
  useEffect(() => {
    const loadReport = async () => {
      if (!reportId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const data = await reportService.getReportById(reportId);
        console.log('Reporte cargado:', data);
        setReport(data);
      } catch (error) {
        console.error(`Error al cargar el reporte ${reportId}:`, error);
        setError('No se pudo cargar el reporte. Por favor, intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };
    
    loadReport();
  }, [reportId]);

  // Formatear la fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener el color según el estado del reporte
  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#f39c12'; // Amarillo
      case 'IN_PROGRESS':
        return '#3498db'; // Azul
      case 'COMPLETED':
        return '#2ecc71'; // Verde
      case 'REJECTED':
        return '#e74c3c'; // Rojo
      default:
        return '#7f8c8d'; // Gris
    }
  };

  // Obtener el texto según el estado del reporte
  const getStatusText = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'IN_PROGRESS':
        return 'En Progreso';
      case 'COMPLETED':
        return 'Completado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return 'Desconocido';
    }
  };

  // Alternar la expansión de una sección
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Función para agregar comentario
  const handleAddComment = async () => {
    console.log('handleAddComment called with:', { reportId, commentText });
    
    if (!commentText.trim()) {
      console.log('Comment text is empty, returning');
      return;
    }
    
    setAddingComment(true);
    try {
      console.log('Calling reportService.addComment...');
      const result = await reportService.addComment(reportId, commentText.trim());
      console.log('addComment result:', result);
      
      // Recargar el reporte para mostrar el nuevo comentario
      console.log('Reloading report...');
      const updatedReport = await reportService.getReportById(reportId);
      console.log('Updated report:', updatedReport);
      setReport(updatedReport);
      
      // Cerrar modal y limpiar
      setShowCommentModal(false);
      setCommentText('');
      
      Alert.alert('Éxito', 'Comentario agregado correctamente');
    } catch (error) {
      console.error('Error al agregar comentario:', error);
      Alert.alert('Error', `No se pudo agregar el comentario: ${error.message}`);
    } finally {
      setAddingComment(false);
    }
  };
  
  // Función para exportar reporte
  const handleExportReport = async () => {
    setExporting(true);
    try {
      await reportService.exportReport(reportId);
      Alert.alert('Éxito', 'Reporte exportado correctamente');
    } catch (error) {
      console.error('Error al exportar reporte:', error);
      Alert.alert('Error', `No se pudo exportar el reporte: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#2c3e50" />
        <Text style={styles.loadingText}>Cargando reporte...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!report) {
    return (
      <View style={styles.centered}>
        <Text>No se encontró el reporte</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text style={styles.title}>{report.title || `Reporte #${report.id}`}</Text>
          <TouchableOpacity 
            style={[styles.exportButton, exporting && styles.disabledButton]}
            onPress={handleExportReport}
            disabled={exporting}
          >
            {exporting ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <>
                <Ionicons name="download-outline" size={16} color="white" />
                <Text style={styles.exportButtonText}>Exportar</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(report.status) }]}>
          <Text style={styles.statusText}>{getStatusText(report.status)}</Text>
        </View>
        
        <ReportTags 
          tags={report.tags} 
          onTagPress={(tag) => console.log('Tag pressed:', tag)}
          style={styles.headerTags}
        />
      </View>
      
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Información General</Text>
        
        <View style={styles.infoRow}>
          <Ionicons name="calendar-outline" size={18} color="#7f8c8d" style={styles.icon} />
          <Text style={styles.infoLabel}>Fecha:</Text>
          <Text style={styles.infoValue}>{formatDate(report.created_at)}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <Ionicons name="location-outline" size={18} color="#7f8c8d" style={styles.icon} />
          <Text style={styles.infoLabel}>Misión:</Text>
          <Text style={styles.infoValue}>{report.mission_name || `Misión #${report.mission}`}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <Ionicons name="map-outline" size={18} color="#7f8c8d" style={styles.icon} />
          <Text style={styles.infoLabel}>Ubicación:</Text>
          <Text style={styles.infoValue}>{report.location_name || 'No especificada'}</Text>
        </View>
        
        <View style={styles.infoRow}>
          <Ionicons name="flag-outline" size={18} color="#7f8c8d" style={styles.icon} />
          <Text style={styles.infoLabel}>Objetivos:</Text>
          <View style={styles.objectivesContainer}>
            {report.objectives_details && report.objectives_details.length > 0 ? (
              report.objectives_details.map((objective, index) => (
                <View key={objective.id} style={styles.objectiveItem}>
                  <Text style={styles.objectiveName}>{objective.name}</Text>
                  <Text style={styles.objectiveDescription}>{objective.description}</Text>
                </View>
              ))
            ) : (
              <Text style={styles.criticalText}>Sin objetivo</Text>
            )}
          </View>
        </View>
        
        <View style={styles.infoRow}>
          <Ionicons name="person-outline" size={18} color="#7f8c8d" style={styles.icon} />
          <Text style={styles.infoLabel}>Piloto:</Text>
          {report.pilot_name && report.pilot_id ? (
            <TouchableOpacity 
              onPress={() => {
                navigation.navigate('PilotProfile', {
                  pilotId: report.pilot_id,
                  missionId: report.mission,
                  pilotName: report.pilot_name
                });
              }}
            >
              <Text style={[styles.infoValue, styles.clickableText]}>
                {report.pilot_name}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.infoValue}>No asignado</Text>
          )}
        </View>
        

      </View>
      
      {/* Análisis Multispectral */}
      <View style={styles.section}>
        <TouchableOpacity onPress={() => toggleSection('map')}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Análisis Multispectral</Text>
            <Ionicons 
              name={expandedSections.map ? 'chevron-up' : 'chevron-down'} 
              size={20} 
              color="#7f8c8d" 
            />
          </View>
        </TouchableOpacity>
        
        {expandedSections.map && (
          <View style={styles.mapContainer}>
            <InteractiveMapView 
              tilesUrl={report.tiles_url} 
              ndviTilesUrl={report.findings?.ndvi_tiles_url} 
              ndreTilesUrl={report.findings?.ndre_tiles_url} 
              gndviTilesUrl={report.findings?.gndvi_tiles_url} 
              fieldGeojson={report.field_geojson} 
            />
          </View>
        )}
      </View>
      
      <View style={styles.section}>
        <TouchableOpacity onPress={() => toggleSection('findings')}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Bitácora de Hallazgos</Text>
            <Ionicons 
              name={expandedSections.findings ? 'chevron-up' : 'chevron-down'} 
              size={20} 
              color="#7f8c8d" 
            />
          </View>
        </TouchableOpacity>
        
        {expandedSections.findings && (
          <View style={styles.findingsContainer}>
            {report.findings?.comments && report.findings.comments.length > 0 ? (
              <View>
                <Text style={styles.findingsTitle}>Comentarios:</Text>
                {report.findings.comments.map((comment, index) => (
                  <View key={index} style={styles.commentItem}>
                    <Text style={styles.commentText}>{comment.text}</Text>
                    <Text style={styles.commentMeta}>
                      {comment.user} - {new Date(comment.timestamp).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  </View>
                ))}
              </View>
            ) : (
              <Text style={styles.noCommentsText}>No hay comentarios aún. Agrega el primero.</Text>
            )}
            
            <TouchableOpacity 
              style={styles.addCommentButton}
              onPress={() => setShowCommentModal(true)}
            >
              <Ionicons name="add-circle-outline" size={20} color="#3498db" />
              <Text style={styles.addCommentText}>Agregar comentario</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {report.recommendations && (
        <View style={styles.section}>
          <TouchableOpacity onPress={() => toggleSection('recommendations')}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Recomendaciones</Text>
              <Ionicons 
                name={expandedSections.recommendations ? 'chevron-up' : 'chevron-down'} 
                size={20} 
                color="#7f8c8d" 
              />
            </View>
          </TouchableOpacity>
          
          {expandedSections.recommendations && (
            <Text style={styles.contentText}>{report.recommendations}</Text>
          )}
        </View>
      )}
      
      {report.images && report.images.length > 0 && (
        <View style={styles.section}>
          <TouchableOpacity onPress={() => toggleSection('images')}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Imágenes</Text>
              <Ionicons 
                name={expandedSections.images ? 'chevron-up' : 'chevron-down'} 
                size={20} 
                color="#7f8c8d" 
              />
            </View>
          </TouchableOpacity>
          
          {expandedSections.images && (
            <View style={styles.imagesContainer}>
              {report.images.map((image, index) => (
                <View key={index} style={styles.imageWrapper}>
                  <Image 
                    source={{ uri: image.url }} 
                    style={styles.image} 
                    resizeMode="cover"
                  />
                  {image.caption && (
                    <Text style={styles.imageCaption}>{image.caption}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}
      
      {report.attachments && report.attachments.length > 0 && (
        <View style={styles.section}>
          <TouchableOpacity onPress={() => toggleSection('attachments')}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Archivos Adjuntos</Text>
              <Ionicons 
                name={expandedSections.attachments ? 'chevron-up' : 'chevron-down'} 
                size={20} 
                color="#7f8c8d" 
              />
            </View>
          </TouchableOpacity>
          
          {expandedSections.attachments && (
            <View style={styles.attachmentsContainer}>
              {report.attachments.map((attachment, index) => (
                <TouchableOpacity key={index} style={styles.attachmentItem}>
                  <Ionicons name="document-outline" size={24} color="#3498db" style={styles.attachmentIcon} />
                  <View style={styles.attachmentInfo}>
                    <Text style={styles.attachmentName}>{attachment.name}</Text>
                    <Text style={styles.attachmentType}>{attachment.type}</Text>
                  </View>
                  <Ionicons name="download-outline" size={24} color="#3498db" />
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      )}
      
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Última actualización: {formatDate(report.updated_at || report.created_at)}
        </Text>
      </View>
      
      {/* Modal para agregar comentario */}
      <Modal
        visible={showCommentModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCommentModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Agregar Comentario</Text>
              <TouchableOpacity onPress={() => setShowCommentModal(false)}>
                <Ionicons name="close" size={24} color="#7f8c8d" />
              </TouchableOpacity>
            </View>
            
            <TextInput
              style={styles.commentInput}
              placeholder="Escribe tu comentario sobre los hallazgos..."
              value={commentText}
              onChangeText={setCommentText}
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
            
            <View style={styles.modalActions}>
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => {
                  setShowCommentModal(false);
                  setCommentText('');
                }}
              >
                <Text style={styles.cancelButtonText}>Cancelar</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.saveButton, addingComment && styles.disabledButton]}
                onPress={handleAddComment}
                disabled={addingComment || !commentText.trim()}
              >
                {addingComment ? (
                  <ActivityIndicator size="small" color="white" />
                ) : (
                  <Text style={styles.saveButtonText}>Guardar</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 10,
    color: '#7f8c8d',
    fontSize: 16,
  },
  errorText: {
    color: '#e74c3c',
    fontSize: 16,
    textAlign: 'center',
  },
  header: {
    backgroundColor: 'white',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c3e50',
    flex: 1,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#3498db',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginLeft: 10,
  },
  exportButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 4,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  statusText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  section: {
    backgroundColor: 'white',
    padding: 15,
    marginTop: 10,
    borderRadius: 5,
    marginHorizontal: 10,
    marginBottom: 5,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  icon: {
    marginRight: 8,
  },
  infoLabel: {
    width: 80,
    fontSize: 16,
    color: '#7f8c8d',
  },
  infoValue: {
    flex: 1,
    fontSize: 16,
    color: '#2c3e50',
  },
  summaryText: {
    fontSize: 16,
    color: '#2c3e50',
    lineHeight: 24,
  },
  contentText: {
    fontSize: 16,
    color: '#2c3e50',
    lineHeight: 24,
    marginTop: 10,
  },
  mapContainer: {
    marginTop: 15,
    height: 400,
    width: '100%',
  },
  imagesContainer: {
    marginTop: 10,
  },
  imageWrapper: {
    marginBottom: 15,
  },
  image: {
    width: '100%',
    height: 200,
    borderRadius: 5,
  },
  imageCaption: {
    fontSize: 14,
    color: '#7f8c8d',
    fontStyle: 'italic',
    marginTop: 5,
  },
  attachmentsContainer: {
    marginTop: 10,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderWidth: 1,
    borderColor: '#eee',
    borderRadius: 5,
    marginBottom: 10,
  },
  attachmentIcon: {
    marginRight: 10,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  attachmentType: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  footer: {
    padding: 15,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 12,
    color: '#7f8c8d',
    fontStyle: 'italic',
  },
  objectivesContainer: {
    flex: 1,
  },
  objectiveItem: {
    marginBottom: 8,
    paddingLeft: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#3498db',
  },
  objectiveName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  objectiveDescription: {
    fontSize: 13,
    color: '#7f8c8d',
    marginTop: 2,
  },
  criticalText: {
    color: '#e74c3c',
    fontWeight: 'bold',
  },
  clickableText: {
    color: '#3498db',
    textDecorationLine: 'underline',
  },
  findingsContainer: {
    marginTop: 10,
  },
  findingsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  findingItem: {
    fontSize: 15,
    color: '#27ae60',
    marginBottom: 5,
    paddingLeft: 10,
  },
  findingsNote: {
    fontSize: 14,
    color: '#7f8c8d',
    fontStyle: 'italic',
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#ecf0f1',
  },
  addCommentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 6,
    marginTop: 15,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderStyle: 'dashed',
  },
  addCommentText: {
    color: '#3498db',
    fontSize: 14,
    marginLeft: 8,
    fontWeight: '500',
  },
  commentItem: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 6,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#3498db',
  },
  commentText: {
    fontSize: 14,
    color: '#2c3e50',
    lineHeight: 20,
    marginBottom: 6,
  },
  commentMeta: {
    fontSize: 12,
    color: '#7f8c8d',
    fontStyle: 'italic',
  },
  noCommentsText: {
    fontSize: 14,
    color: '#7f8c8d',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: 15,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    width: '90%',
    maxWidth: 400,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  commentInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 100,
    marginBottom: 20,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cancelButton: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    backgroundColor: '#ecf0f1',
    marginRight: 10,
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#7f8c8d',
    fontSize: 14,
    fontWeight: '500',
  },
  saveButton: {
    flex: 1,
    padding: 12,
    borderRadius: 6,
    backgroundColor: '#3498db',
    alignItems: 'center',
  },
  saveButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
  },
  disabledButton: {
    backgroundColor: '#bdc3c7',
  },
  headerTags: {
    marginTop: 10,
  },
});

export default ReportDetailScreen;